import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, CircularProgress
} from '@mui/material';
import { ApiRequest } from 'GlobalFunctions';

interface Sibling {
  product_id: number;
  product_name: string;
  product_code: string;
  stock: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  productId: number;
  warehouseId: string;
  orderProducts: { product_id: number; quantity: number }[];
  onAdd: (product: { product_id: number; product_name: string; product_code: string; quantity: number }) => void;
  /** Product IDs already in the substitution list to exclude or show remaining */
  existingSubstitutes: { product_id: number; quantity: number }[];
  /** Remaining quantity still needed to complete the substitution */
  remainingToAssign: number;
}

export default function AddSiblingProductModal({ open, onClose, productId, warehouseId, orderProducts, onAdd, existingSubstitutes, remainingToAssign }: Props) {
  const [siblings, setSiblings] = useState<Sibling[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Sibling | null>(null);
  const [qty, setQty] = useState('');

  useEffect(() => {
    if (!open) { setSiblings([]); setSelected(null); setQty(''); return; }
    setLoading(true);
    ApiRequest({
      url: '/products/siblings',
      method: 'post',
      headers: { ltkn: localStorage.getItem('ltkn') },
      body: { product_id: productId, warehouse_id: warehouseId, order_products: orderProducts },
      setResponse: (r: any) => {
        if (r.status === 'success') setSiblings(r.siblings || []);
        setLoading(false);
      },
    });
  }, [open]);

  const getAvailableStock = (s: Sibling) => {
    const used = existingSubstitutes.find(e => e.product_id === s.product_id)?.quantity || 0;
    return Math.max(0, s.stock - used);
  };

  const maxQty = selected ? Math.min(getAvailableStock(selected), remainingToAssign) : 0;

  const handleConfirm = () => {
    if (!selected || !qty || Number(qty) <= 0) return;
    onAdd({ product_id: selected.product_id, product_name: selected.product_name, product_code: selected.product_code, quantity: Number(qty) });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agregar producto hermano</DialogTitle>
      <DialogContent>
        <div style={{ marginBottom: 12, padding: '8px 12px', backgroundColor: '#e3f2fd', borderRadius: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: '#1565c0' }}>
            Cantidad faltante por asignar: <strong>{remainingToAssign}</strong>
          </span>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><CircularProgress size={24} /></div>
        ) : !selected ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell>
                <TableCell align="center">Disponible</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {siblings.map(s => {
                const avail = getAvailableStock(s);
                return (
                  <TableRow key={s.product_id}>
                    <TableCell>{s.product_name} <span style={{ color: '#888', fontSize: 12 }}>{s.product_code}</span></TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>{avail}</TableCell>
                    <TableCell align="center">
                      <Button variant="contained" size="small" disabled={avail <= 0} onClick={() => setSelected(s)}>Seleccionar</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!siblings.length && <TableRow><TableCell colSpan={3} align="center">No hay productos hermanos</TableCell></TableRow>}
            </TableBody>
          </Table>
        ) : (
          <div style={{ padding: '16px 0' }}>
            <p style={{ margin: '0 0 12px', fontWeight: 500 }}>{selected.product_name} <span style={{ color: '#888', fontSize: 12 }}>{selected.product_code}</span></p>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: '#555' }}>Disponible: {maxQty}</p>
            <TextField
              label="Cantidad"
              type="number"
              size="small"
              value={qty}
              onChange={e => setQty(String(Math.min(Number(e.target.value), maxQty)))}
              inputProps={{ min: 1, max: maxQty }}
              fullWidth
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {selected && <Button onClick={() => setSelected(null)}>Volver</Button>}
        <Button onClick={onClose}>Cancelar</Button>
        {selected && <Button variant="contained" disabled={!qty || Number(qty) <= 0} onClick={handleConfirm}>Agregar</Button>}
      </DialogActions>
    </Dialog>
  );
}
